import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { toast } from "sonner";
import { History, Menu, X, Sparkles, Settings, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import CommandPalette from "@/components/CommandPalette";
import NotificationCenter from "@/components/NotificationCenter";
import BrandSettings from "@/components/BrandSettings";
import { secureStorage } from "@/lib/secureStorage";
import { ContenidoDropdown, HerramientasDropdown, MonitoreoDropdown } from "./navbar/NavbarDropdowns";
import { UserMenu } from "./navbar/UserMenu";
import { MobileMenu } from "./navbar/MobileMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const MASTER_ADMIN_EMAIL = "hello@proximalbs.com";

// Auth Handler Component
const AuthHandler = ({ onAuthComplete }) => {
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const didRun = React.useRef(false);

  const handleRetryLogin = () => {
    const currentDomain = window.location.origin;
    const authUrl = `${process.env.REACT_APP_EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(currentDomain + '/dashboard')}`;
    window.location.href = authUrl;
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const handleAuth = async (attempt = 0) => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        window.location.href = "/";
        return;
      }

      try {
        setStatus('authenticating');
        const response = await axios.post(
          `${API}/auth/session`,
          {},
          { headers: { "X-Session-ID": sessionId }, timeout: 20000 }
        );

        if (response.data.user) {
          setStatus('success');
          secureStorage.setUser(response.data.user);
          if (response.data.session_token) {
            secureStorage.setToken(response.data.session_token);
          }
          toast.success(`Bienvenido, ${response.data.user.name || response.data.user.email}!`);
          onAuthComplete(response.data.user);
        } else {
          throw new Error('No user data received');
        }
      } catch (error) {
        if (attempt < 2 && (error.code === 'ECONNABORTED' || error.response?.status >= 500)) {
          setRetryCount(attempt + 1);
          setTimeout(() => handleAuth(attempt + 1), 1500);
          return;
        }

        setStatus('error');
        const detail = error.response?.data?.detail || error.message || "Error desconocido";
        if (error.response?.status === 401 || detail.includes("expiró") || detail.includes("expired")) {
          setErrorMessage("La sesion de autenticacion expiro. Intenta de nuevo.");
        } else if (error.response?.status === 503 || error.code === 'ECONNABORTED') {
          setErrorMessage("No se pudo conectar con el servidor. Verifica tu conexion.");
        } else {
          setErrorMessage(detail);
        }
        toast.error("Error de autenticacion.");
      }
    };

    handleAuth();
  }, [onAuthComplete]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Error de autenticación</h2>
          <p className="text-gray-400 mb-6">{errorMessage}</p>
          <div className="space-y-3">
            <button
              onClick={handleRetryLogin}
              className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-colors"
            >
              Intentar de nuevo
            </button>
            <button
              onClick={() => {
                window.history.replaceState({}, document.title, "/");
                window.location.href = "/";
              }}
              className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition-colors"
            >
              Volver al inicio
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Si el problema persiste, intenta limpiar las cookies de tu navegador o usa una ventana de incógnito.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
        <p className="text-gray-400">
          {status === 'processing' && 'Procesando...'}
          {status === 'authenticating' && 'Autenticando con Google...'}
          {status === 'success' && '¡Bienvenido! Redirigiendo...'}
        </p>
        {retryCount > 0 && (
          <p className="text-xs text-gray-500 mt-2">Reintentando conexión...</p>
        )}
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children, user, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (user.email !== MASTER_ADMIN_EMAIL) {
    return <Navigate to="/coming-soon" replace />;
  }

  return children;
};

const Navbar = ({ user, onLogout, realtimeUpdates }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBrandSettings, setShowBrandSettings] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  const brandLogo = localStorage.getItem('brand_logo');
  const brandName = localStorage.getItem('brand_company_name');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      onLogout();
      navigate("/");
      toast.success("Sesión cerrada");
    } catch (error) {
      onLogout();
      navigate("/");
    }
  };

  const handleNavigate = (path) => {
    setOpenDropdown(null);
    navigate(path);
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown && !e.target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const dropdownProps = { openDropdown, setOpenDropdown, toggleDropdown, handleNavigate };

  return (
    <nav
      className="fixed top-0 w-full z-50 transition-all duration-300"
      style={{
        background: scrolled ? 'rgba(3,0,20,0.85)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.08)' : '1px solid transparent',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <div className="flex items-center space-x-8">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-3 transition-transform hover:scale-105"
              data-testid="logo-button"
            >
              <img
                src={brandLogo || "/proxima-labs-logo.png"}
                alt={brandName || "Próxima Labs"}
                className="w-40 sm:w-48 h-auto object-contain"
              />
            </button>
            {user && (
              <nav className="hidden md:flex items-center space-x-1">
                <button
                  onClick={() => handleNavigate("/dashboard")}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="nav-dashboard-link"
                >
                  Dashboard
                </button>

                <button
                  onClick={() => {
                    setOpenDropdown(null);
                    window.dispatchEvent(new CustomEvent("proxima:open-officers"));
                  }}
                  className="px-3 py-2 text-sm text-purple-300 hover:text-white rounded-lg transition-colors flex items-center gap-1.5 font-semibold bg-purple-500/10 border border-purple-500/25 hover:bg-purple-500/20"
                  data-testid="nav-ai-officers-link"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  AI Officers
                </button>

                <ContenidoDropdown {...dropdownProps} />
                <HerramientasDropdown {...dropdownProps} />
                <MonitoreoDropdown {...dropdownProps} />

                <button
                  onClick={() => handleNavigate("/ai-analytics")}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="nav-ai-analytics-link"
                >
                  Analytics
                </button>

                <button
                  onClick={() => handleNavigate("/pricing")}
                  className="px-3 py-2 text-sm text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 rounded-lg transition-colors font-medium"
                  data-testid="nav-pricing-link"
                >
                  Planes
                </button>
              </nav>
            )}
          </div>

          {user && (
            <div className="hidden md:flex items-center space-x-4">
              <CommandPalette user={user} onLogout={handleLogout} />
              {realtimeUpdates && (
                <NotificationCenter
                  notifications={realtimeUpdates.notifications}
                  unreadCount={realtimeUpdates.unreadCount}
                  onMarkAsRead={realtimeUpdates.markAsRead}
                  onMarkAllAsRead={realtimeUpdates.markAllAsRead}
                  onClearAll={realtimeUpdates.clearAll}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/history")}
                className="text-gray-400 hover:text-white"
                title="Historial"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/brand-profile")}
                className="text-gray-400 hover:text-white"
                title="Perfil de Marca"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <UserMenu
                user={user}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                toggleDropdown={toggleDropdown}
                handleNavigate={handleNavigate}
                handleLogout={handleLogout}
              />
            </div>
          )}

          {user && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-300"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          )}
        </div>
      </div>

      {mobileMenuOpen && user && (
        <MobileMenu
          navigate={navigate}
          onClose={() => setMobileMenuOpen(false)}
          handleLogout={handleLogout}
        />
      )}

      {showBrandSettings && (
        <BrandSettings onClose={() => setShowBrandSettings(false)} />
      )}
    </nav>
  );
};

export { AuthHandler, ProtectedRoute, Navbar };
